import React, { useCallback, useState } from "react";
import { Trash2, Archive, ArchiveRestore, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { apiError } from "@/lib/api";
import { trashAction } from "@/lib/trash";

export function useSelect() {
  const [selected, setSelected] = useState(() => new Set());
  const toggle = useCallback((id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  }), []);
  const clear = useCallback(() => setSelected(new Set()), []);
  const setAll = useCallback((ids, on) => setSelected(on ? new Set(ids) : new Set()), []);
  const has = useCallback((id) => selected.has(id), [selected]);
  return { selected, ids: [...selected], count: selected.size, toggle, clear, setAll, has };
}

export function SelectCheck({ checked, onChange, testId, className = "" }) {
  return (
    <span className={className} onClick={(e) => e.stopPropagation()}>
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </span>
  );
}

export function SelectionToolbar({ items, idKey, sel, entity, onDone, archived = false, onToggleArchived, canArchive = true, label = "élément" }) {
  const ids = items.map((i) => i[idKey]);
  const allChecked = ids.length > 0 && ids.every((id) => sel.has(id));

  const run = async (action) => {
    if (!sel.count) return;
    if (action === "delete" && !window.confirm(`Déplacer ${sel.count} ${label}(s) vers la corbeille ?`)) return;
    try {
      const { data } = await trashAction(entity, sel.ids, action);
      const msg = { delete: "déplacé(s) vers la corbeille", archive: "archivé(s)", unarchive: "désarchivé(s)" }[action];
      toast.success(`${data.affected} ${label}(s) ${msg}`);
      sel.clear();
      onDone && onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="selection-toolbar">
      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
        <Checkbox checked={allChecked} onCheckedChange={(v) => sel.setAll(ids, v)} data-testid="select-all" />
        Tout sélectionner
      </label>
      {sel.count > 0 && <span className="text-sm font-medium text-slate-900">· {sel.count} sélectionné(s)</span>}
      <div className="flex-1" />
      {sel.count > 0 && <>
        {archived
          ? <Button size="sm" variant="outline" onClick={() => run("unarchive")} data-testid="bulk-unarchive"><ArchiveRestore className="h-4 w-4 mr-1" />Désarchiver</Button>
          : (canArchive && <Button size="sm" variant="outline" onClick={() => run("archive")} data-testid="bulk-archive"><Archive className="h-4 w-4 mr-1" />Archiver</Button>)}
        <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => run("delete")} data-testid="bulk-delete"><Trash2 className="h-4 w-4 mr-1" />Supprimer</Button>
        <button onClick={sel.clear} className="text-slate-400 hover:text-slate-600" data-testid="bulk-clear"><X className="h-4 w-4" /></button>
      </>}
      {onToggleArchived && (
        <button onClick={onToggleArchived} data-testid="toggle-archived"
          className={`text-sm px-2.5 py-1 rounded-md border ${archived ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
          {archived ? "Archivés" : "Actifs"}
        </button>
      )}
    </div>
  );
}
